import { Fragment, useState } from "react";
import type { FunnelReport, FunnelStep } from "@/api";
import { fmtMs } from "@/utils/format";

export type Denominator = "answered" | "started";

function Pct({ pct, tone = "lilac" }: { pct: number | null | undefined; tone?: "lilac" | "coral" }) {
  if (pct == null) return <span className="text-faint">—</span>;
  return (
    <span className="flex items-center justify-end gap-2.5">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-3">
        <span
          className={`block h-full rounded-full ${tone === "coral" ? "bg-coral" : "bg-lilac"}`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </span>
      <span className="w-12 text-right tabular-nums text-dim">{pct}%</span>
    </span>
  );
}

function Breakdown({ step, denominator }: { step: FunnelStep; denominator: Denominator }) {
  if (step.responses.length === 0) {
    return (
      <p className="px-5 py-3 text-sm text-faint">
        {step.question_type === "text"
          ? "Free-text answers are not collected in analytics (privacy)."
          : "No response data for this step."}
      </p>
    );
  }
  const top = Math.max(...step.responses.map((r) => r.count), 1);
  return (
    <div className="space-y-2 px-5 py-3">
      {step.responses.map((r) => {
        const pct = denominator === "answered" ? r.pct_of_answered : r.pct_of_started ?? null;
        return (
          <div key={r.answer_id} className="flex items-center gap-3 text-sm">
            <span className="w-56 truncate text-dim" title={r.label ?? r.answer_id}>
              {r.label ?? r.answer_id}
            </span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-ink-3">
              <span
                className={`block h-full rounded-full ${r.count === top ? "bg-lime" : "bg-lilac"}`}
                style={{ width: `${top > 0 ? (r.count / top) * 100 : 0}%` }}
              />
            </span>
            <span className="w-24 text-right tabular-nums text-text">
              {r.count} <span className="text-faint">{pct == null ? "" : `· ${pct}%`}</span>
            </span>
          </div>
        );
      })}
      {step.question_type === "multi" && (
        <p className="text-xs text-faint">Multi-select: sessions pick several options, so shares can exceed 100%.</p>
      )}
    </div>
  );
}

export function PagesTable({
  report,
  denominator,
}: {
  report: FunnelReport;
  denominator: Denominator;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (qid: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });

  const firstViewed = report.steps[0]?.viewed ?? 0;
  const startExits = Math.max(0, report.started - firstViewed);
  const startDrop =
    report.started > 0 ? Math.round((startExits / report.started) * 1000) / 10 : null;

  return (
    <div className="card overflow-hidden">
      <table className="table-asteroi">
        <thead>
          <tr>
            <th className="px-5">Page</th>
            <th>Viewed</th>
            <th>Answered</th>
            <th>Exits</th>
            <th>Time on page</th>
            <th className="pr-5 text-right">Drop-off</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-5 font-semibold text-text">Quiz start</td>
            <td className="tabular-nums">{report.started}</td>
            <td className="text-faint">—</td>
            <td className="tabular-nums">{startExits}</td>
            <td className="text-faint">—</td>
            <td className="pr-5 text-right">
              <Pct pct={startDrop} tone="coral" />
            </td>
          </tr>
          {report.steps.map((s) => (
            <Fragment key={s.question_id}>
              <tr>
                <td className="px-5">
                  <button
                    type="button"
                    className="text-left hover:text-text"
                    aria-expanded={open.has(s.question_id)}
                    onClick={() => toggle(s.question_id)}
                  >
                    <span className="text-text">{s.label ?? s.question_id}</span>
                    {s.label && <span className="ml-2 font-mono text-xs text-faint">{s.question_id}</span>}
                  </button>
                </td>
                <td className="tabular-nums">{s.viewed}</td>
                <td className="tabular-nums">{s.answered}</td>
                <td className="tabular-nums">{s.exits ?? "—"}</td>
                <td className="tabular-nums text-dim">{fmtMs(s.time_on_page_ms)}</td>
                <td className="pr-5 text-right">
                  <Pct pct={s.drop_off_pct} tone="coral" />
                </td>
              </tr>
              {open.has(s.question_id) && (
                <tr>
                  <td colSpan={6} className="bg-ink-1/40">
                    <Breakdown step={s} denominator={denominator} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr>
            <td className="px-5 font-semibold text-lime">Completion</td>
            <td className="tabular-nums">{report.completed}</td>
            <td className="text-faint">—</td>
            <td className="text-faint">—</td>
            <td className="tabular-nums text-dim">{fmtMs(report.avg_duration_ms)}</td>
            <td className="pr-5 text-right tabular-nums text-dim">
              {report.completion_rate == null ? "—" : `${report.completion_rate}% of starts`}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
